import { getApiV1Url } from '@/config/api'

export { getTodayString } from '@/utils/datetime'

/** 회원 수 API 응답 data */
export interface MembersCountTodayData {
  todayCount: number
  yesterdayCount: number
  totalCount: number
}

/**
 * 적립 수 API를 화면/통계에서 쓰기 위한 정규화 형태
 * (reward-stats/today-summary 응답의 todayEarnCount·yesterdayEarnCount를 매핑)
 */
export interface StampsCountEarnTodayYesterdayData {
  todayCount: number
  yesterdayCount: number
}

/** reward-stats/today-summary API 응답 data */
interface RewardStatsTodaySummaryData {
  todayEarnCount: number
  yesterdayEarnCount: number
}

/** coupon-stats/today-summary dateField */
export type CouponStatsDateField = 'ISSUED_AT' | 'USED_AT' | 'EXPIRE_AT'

/** coupon-stats/today-summary 요청 파라미터 */
export interface CouponStatsTodaySummaryParams {
  status?: string
  dateField: CouponStatsDateField
  startAt: string
  endAt: string
}

/** coupon-stats/today-summary API 응답 data */
export interface CouponStatsTodaySummaryData {
  todayCount: number
  yesterdayCount: number
}

/** 대시보드 통계 preset */
export const COUPON_STATS_PRESET = {
  USED: { status: 'USED', dateField: 'USED_AT' },
  ISSUED: { dateField: 'ISSUED_AT' },
  EXPIRED: { dateField: 'EXPIRE_AT' }
} as const satisfies Record<string, Pick<CouponStatsTodaySummaryParams, 'status' | 'dateField'>>

/** 서버 공통 응답 (data만 사용) */
interface ApiResponse<T> {
  data?: T
  errors?: {
    error?: Array<{ code?: number; target?: string; message?: string }>
  }
}

/**
 * 어제 대비 변화율(%) 계산. 어제가 0이면 null.
 */
export function calcChangePercent(
  todayCount: number,
  yesterdayCount: number
): number | null {
  if (yesterdayCount === 0) return null
  return Math.round(((todayCount - yesterdayCount) / yesterdayCount) * 100)
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/shop-customer-stats/today-summary
 * @param startAt ISO-8601 (예: 2026-04-17T00:00:00+09:00)
 * @param endAt ISO-8601 (예: 2026-04-18T00:00:00+09:00)
 * @deprecated 대시보드는 total + daily 조합 사용. 호환용으로 유지.
 */
export async function getMembersCountToday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<MembersCountTodayData> {
  const query = new URLSearchParams({
    startAt,
    endAt
  }).toString()
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/shop-customer-stats/today-summary?${query}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MembersCountTodayData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('회원 수 데이터를 불러올 수 없습니다.')
  }

  return json.data
}

/** shop-customer-stats/count 응답 data */
export interface MembersStatsCountData {
  count: number
}

/** count 조회 기간. startDate/endDate 모두 빈 값이면 전체 회원 수 */
export interface MembersStatsCountParams {
  startDate?: string
  endDate?: string
  /** IANA TZ (예: Asia/Seoul). project lookup timezone */
  timezone?: string | null
}

function appendTimezoneParam(
  query: URLSearchParams,
  timezone?: string | null
): void {
  const tz = timezone?.trim()
  if (tz) query.set('timezone', tz)
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/shop-customer-stats/count
 * - startDate/endDate 있음: 기간 내 신규 회원 수 (inclusive/exclusive)
 * - 둘 다 빈 값: 전체 회원 수
 */
export async function getMembersStatsCount(
  projectCode: string,
  shopCode: string,
  params: MembersStatsCountParams = {}
): Promise<MembersStatsCountData> {
  const query = new URLSearchParams({
    startDate: params.startDate ?? '',
    endDate: params.endDate ?? ''
  })
  appendTimezoneParam(query, params.timezone)
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/shop-customer-stats/count?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MembersStatsCountData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null || typeof json.data.count !== 'number') {
    throw new Error('회원 수를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/reward-stats/today-summary
 * @param startAt ISO-8601 (예: 2026-04-17T00:00:00+09:00)
 * @param endAt ISO-8601 (예: 2026-04-18T00:00:00+09:00)
 */
export async function getStampsCountEarnTodayYesterday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<StampsCountEarnTodayYesterdayData> {
  const query = new URLSearchParams({
    startAt,
    endAt
  }).toString()
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/reward-stats/today-summary?${query}`
  )
  const response = await fetch(url)
  const json: ApiResponse<RewardStatsTodaySummaryData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('적립 수 데이터를 불러올 수 없습니다.')
  }

  const { todayEarnCount, yesterdayEarnCount } = json.data
  if (
    typeof todayEarnCount !== 'number' ||
    typeof yesterdayEarnCount !== 'number'
  ) {
    throw new Error('적립 수 데이터를 불러올 수 없습니다.')
  }

  return {
    todayCount: todayEarnCount,
    yesterdayCount: yesterdayEarnCount
  }
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/coupon-stats/today-summary
 */
export async function getCouponStatsTodaySummary(
  projectCode: string,
  shopCode: string,
  params: CouponStatsTodaySummaryParams
): Promise<CouponStatsTodaySummaryData> {
  const query = new URLSearchParams({
    dateField: params.dateField,
    startAt: params.startAt,
    endAt: params.endAt
  })
  if (params.status != null) {
    query.set('status', params.status)
  }

  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/coupon-stats/today-summary?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<CouponStatsTodaySummaryData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('쿠폰 통계 데이터를 불러올 수 없습니다.')
  }

  const { todayCount, yesterdayCount } = json.data
  if (typeof todayCount !== 'number' || typeof yesterdayCount !== 'number') {
    throw new Error('쿠폰 통계 데이터를 불러올 수 없습니다.')
  }

  return json.data
}

/** 오늘 쿠폰 사용 건수 (status=USED, dateField=USED_AT) */
export async function getCouponUsedCountTodayYesterday(
  projectCode: string,
  shopCode: string,
  startAt: string,
  endAt: string
): Promise<CouponStatsTodaySummaryData> {
  return getCouponStatsTodaySummary(projectCode, shopCode, {
    ...COUPON_STATS_PRESET.USED,
    startAt,
    endAt
  })
}

/** daily API 공통: startDate inclusive, endDate exclusive (YYYY-MM-DD) */
export interface StatsDailyRangeParams {
  startDate: string
  endDate: string
  /** IANA TZ (예: Asia/Seoul). project lookup timezone */
  timezone?: string | null
}

export interface MembersStatsDailyPoint {
  date: string
  newCount: number
}

export interface MembersStatsDailyData {
  points: MembersStatsDailyPoint[]
}

export interface RewardStatsDailyPoint {
  date: string
  earnCount: number
}

export interface RewardStatsDailyData {
  points: RewardStatsDailyPoint[]
}

export interface CouponStatsDailyPoint {
  date: string
  count: number
}

export interface CouponStatsDailyData {
  points: CouponStatsDailyPoint[]
}

export interface CouponStatsDailyParams extends StatsDailyRangeParams {
  dateField: CouponStatsDateField
  status?: string
}

/** YYYY-MM-DD에 days를 더한 날짜 (달력 일자 연산, TZ 무관) */
export function addDaysToDateString(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const next = new Date(y, m - 1, d + days)
  const yy = next.getFullYear()
  const mm = String(next.getMonth() + 1).padStart(2, '0')
  const dd = String(next.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/** 해당 월 1일 (YYYY-MM-DD) */
export function getMonthStartString(dateStr: string): string {
  const [y, m] = dateStr.split('-')
  return `${y}-${m}-01`
}

/**
 * 최근 N일 조회용 구간.
 * endDate는 exclusive → 오늘 포함 N일이면 start=오늘-(N-1), end=오늘+1
 */
export function getLastNDaysExclusiveRange(
  today: string,
  days: number
): StatsDailyRangeParams {
  return {
    startDate: addDaysToDateString(today, -(days - 1)),
    endDate: addDaysToDateString(today, 1)
  }
}

/** 이번 달 1일 ~ 오늘 포함 (endDate exclusive = 내일) */
export function getMonthToDateExclusiveRange(today: string): StatsDailyRangeParams {
  return {
    startDate: getMonthStartString(today),
    endDate: addDaysToDateString(today, 1)
  }
}

function sumCounts(values: number[]): number {
  return values.reduce((acc, n) => acc + n, 0)
}

/** points에서 특정 일자 count 조회. 없으면 0 */
export function findDailyCountByDate<T extends { date: string }>(
  points: T[],
  date: string,
  getCount: (p: T) => number
): number {
  const found = points.find((p) => p.date === date)
  return found ? getCount(found) : 0
}

export function sumDailyCounts<T>(
  points: T[],
  getCount: (p: T) => number
): number {
  return sumCounts(points.map(getCount))
}

/**
 * GET .../shop-customer-stats/daily
 * startDate inclusive, endDate exclusive
 */
export async function getMembersStatsDaily(
  projectCode: string,
  shopCode: string,
  params: StatsDailyRangeParams
): Promise<MembersStatsDailyData> {
  const query = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate
  })
  appendTimezoneParam(query, params.timezone)
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/shop-customer-stats/daily?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MembersStatsDailyData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }
  if (json.data?.points == null || !Array.isArray(json.data.points)) {
    throw new Error('회원 일별 통계를 불러올 수 없습니다.')
  }
  return json.data
}

/**
 * GET .../reward-stats/daily
 * startDate inclusive, endDate exclusive
 */
export async function getRewardStatsDaily(
  projectCode: string,
  shopCode: string,
  params: StatsDailyRangeParams
): Promise<RewardStatsDailyData> {
  const query = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate
  })
  appendTimezoneParam(query, params.timezone)
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/reward-stats/daily?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<RewardStatsDailyData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }
  if (json.data?.points == null || !Array.isArray(json.data.points)) {
    throw new Error('적립 일별 통계를 불러올 수 없습니다.')
  }
  return json.data
}

/**
 * GET .../coupon-stats/daily
 * startDate inclusive, endDate exclusive
 */
export async function getCouponStatsDaily(
  projectCode: string,
  shopCode: string,
  params: CouponStatsDailyParams
): Promise<CouponStatsDailyData> {
  const query = new URLSearchParams({
    dateField: params.dateField,
    startDate: params.startDate,
    endDate: params.endDate
  })
  if (params.status != null) {
    query.set('status', params.status)
  }
  appendTimezoneParam(query, params.timezone)

  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/coupon-stats/daily?${query.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<CouponStatsDailyData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }
  if (json.data?.points == null || !Array.isArray(json.data.points)) {
    throw new Error('쿠폰 일별 통계를 불러올 수 없습니다.')
  }
  return json.data
}

/** 오늘 쿠폰 사용 일별 (USED + USED_AT) */
export async function getCouponUsedStatsDaily(
  projectCode: string,
  shopCode: string,
  params: StatsDailyRangeParams
): Promise<CouponStatsDailyData> {
  return getCouponStatsDaily(projectCode, shopCode, {
    ...COUPON_STATS_PRESET.USED,
    ...params
  })
}
